/* eslint-disable no-magic-numbers -- canvas layout and floating-point tolerances */
import { UnreachableCaseError } from 'ts-essentials';
import type {
  ITextObject,
  TextHorizontalAttachment,
  TextVerticalAttachment,
} from './types/text-object';
import {
  textAppearanceEqual,
  textTransformEqual,
} from './text-object-equality';
import {
  layoutMTextBlock,
  layoutVerticalMTextBlock,
  type MeasureLineContext,
  type MTextBlockLayout,
} from './mtext-layout';
import { isStrokeFont, resolveStrokeFontCss } from './stroke-font-map';
import {
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Texture,
} from '@technology/web3d-plugin-threejs';

/** Three.js `DoubleSide` (2); not re-exported by vitest browser dependency bundles. */
const TEXT_MESH_MATERIAL_SIDE = 2;

export const CANVAS_PADDING = 4;
const MIN_PLANE_SIZE = 0.01;
const DEFAULT_FONT_FAMILY = 'Arial, sans-serif';
/** Canvas resolution only; world size is driven by {@link ITextObject.textHeight}. */
const CANVAS_PIXELS_PER_MODEL_UNIT = 64;
/** Stroke fonts are thin single-stroke; higher resolution keeps them sharp. */
const STROKE_FONT_PIXELS_PER_MODEL_UNIT = 128;
const MIN_CANVAS_FONT_PX = 16;
/** Draw above depth-tested scene geometry (lines, surfaces, meshes). */
const TEXT_RENDER_ORDER = 5;
const GEOMETRY_SIZE_EPSILON = 1e-6;

export interface TextMeshMetrics {
  readonly width: number;
  readonly height: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

export interface TextMeshLineLayout {
  readonly text: string;
  readonly canvasX: number;
  readonly canvasBaselineY: number;
}

export interface TextMeshLayout extends TextMeshMetrics {
  readonly lines: readonly TextMeshLineLayout[];
  readonly canvasFontPx: number;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
}

let sharedMeasureCanvas: HTMLCanvasElement | undefined;
let sharedMeasureContext: CanvasRenderingContext2D | undefined;

function getSharedMeasureContext(): CanvasRenderingContext2D | undefined {
  if (!sharedMeasureCanvas) {
    sharedMeasureCanvas = document.createElement('canvas');
    sharedMeasureContext = sharedMeasureCanvas.getContext('2d') ?? undefined;
  }
  return sharedMeasureContext;
}

function resolveCssFontFamily(typefaceName?: string): string {
  if (!typefaceName?.trim()) {
    return DEFAULT_FONT_FAMILY;
  }
  const strokeCss = resolveStrokeFontCss(typefaceName);
  if (strokeCss) {
    return strokeCss;
  }
  return `${typefaceName.trim()}, ${DEFAULT_FONT_FAMILY}`;
}

function resolveCanvasFontPx(canvasFontPx: number): number {
  return Math.max(MIN_CANVAS_FONT_PX, Math.round(canvasFontPx));
}

function buildCanvasFont(canvasFontPx: number, typefaceName?: string): string {
  const px = resolveCanvasFontPx(canvasFontPx);
  return `${px}px ${resolveCssFontFamily(typefaceName)}`;
}

function resolveWidthFactor(widthFactor: number): number {
  return widthFactor > 1e-12 ? widthFactor : 1;
}

export function computeAttachmentOffsets(
  metrics: Pick<TextMeshMetrics, 'width' | 'height'> & {
    readonly baselineOffsetFromBottom?: number;
  },
  horizontal: TextHorizontalAttachment,
  vertical: TextVerticalAttachment
): Pick<TextMeshMetrics, 'offsetX' | 'offsetY'> {
  let offsetX = 0;
  let offsetY = 0;

  switch (horizontal) {
    case 'left':
      offsetX = 0;
      break;
    case 'center':
      offsetX = -metrics.width / 2;
      break;
    case 'right':
      offsetX = -metrics.width;
      break;
    default:
      throw new UnreachableCaseError(horizontal);
  }

  switch (vertical) {
    case 'baseline':
      offsetY = -(metrics.baselineOffsetFromBottom ?? 0);
      break;
    case 'bottom':
      offsetY = 0;
      break;
    case 'middle':
      offsetY = -metrics.height / 2;
      break;
    case 'top':
      offsetY = -metrics.height;
      break;
    default:
      throw new UnreachableCaseError(vertical);
  }

  return { offsetX, offsetY };
}

function createMeasureContext(
  canvasContext: CanvasRenderingContext2D,
  widthFactor: number
): MeasureLineContext {
  return {
    measureWidth: (text: string) => {
      const metrics = canvasContext.measureText(text || ' ');
      return (
        (metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight) *
        widthFactor
      );
    },
    measureAscentDescent: (text: string) => {
      const metrics = canvasContext.measureText(text || ' ');
      return {
        ascent: metrics.actualBoundingBoxAscent,
        descent: metrics.actualBoundingBoxDescent,
      };
    },
  };
}

function emptyLayout(
  worldHeight: number,
  canvasFontPx: number
): TextMeshLayout {
  return {
    lines: [
      {
        text: '',
        canvasX: CANVAS_PADDING,
        canvasBaselineY: CANVAS_PADDING + worldHeight,
      },
    ],
    canvasFontPx,
    canvasWidth: Math.ceil(worldHeight + CANVAS_PADDING * 2),
    canvasHeight: Math.ceil(worldHeight + CANVAS_PADDING * 2),
    width: worldHeight,
    height: worldHeight,
    offsetX: 0,
    offsetY: 0,
  };
}

/**
 * Lays out text in world units (meters) using a high-res canvas texture.
 * Supports multiline MTEXT via {@link layoutMTextBlock}.
 */
export function measureTextMesh(textObject: ITextObject): TextMeshLayout {
  const worldHeight = Math.max(textObject.textHeight, MIN_PLANE_SIZE);
  const ppu = isStrokeFont(textObject.typefaceName)
    ? STROKE_FONT_PIXELS_PER_MODEL_UNIT
    : CANVAS_PIXELS_PER_MODEL_UNIT;
  const canvasFontPx = resolveCanvasFontPx(worldHeight * ppu);

  const context = getSharedMeasureContext();
  if (!context) {
    return emptyLayout(worldHeight, canvasFontPx);
  }

  context.font = buildCanvasFont(canvasFontPx, textObject.typefaceName);
  const measure = createMeasureContext(
    context,
    resolveWidthFactor(textObject.widthFactor)
  );

  const isVertical =
    textObject.drawDirection === 'topToBottom' ||
    textObject.drawDirection === 'bottomToTop';

  const block: MTextBlockLayout = isVertical
    ? layoutVerticalMTextBlock(textObject, measure)
    : layoutMTextBlock(textObject, canvasFontPx, measure);

  const canvasWidth = Math.ceil(
    Math.max(block.canvasWidth, 1) + CANVAS_PADDING * 2
  );
  const canvasHeight = Math.ceil(
    Math.max(block.canvasHeight, 1) + CANVAS_PADDING * 2
  );

  const lineHeightPx = block.ascentPx + block.descentPx;
  const obliqueTan = Math.tan(textObject.obliqueAngle);
  const hasOblique = Math.abs(textObject.obliqueAngle) > 1e-9;
  const obliqueShearPx = hasOblique ? Math.abs(obliqueTan) * lineHeightPx : 0;
  const obliqueLeftPadPx =
    hasOblique && obliqueTan < 0 ? Math.ceil(obliqueShearPx) : 0;
  const obliqueRightPadPx =
    hasOblique && obliqueTan > 0 ? Math.ceil(obliqueShearPx) : 0;
  const paddedCanvasWidth = canvasWidth + obliqueLeftPadPx + obliqueRightPadPx;

  const pixelToWorldX = block.worldWidth / Math.max(block.canvasWidth, 1e-9);
  const obliqueWorldPad =
    (obliqueLeftPadPx + obliqueRightPadPx) * pixelToWorldX;
  const worldWidth = block.worldWidth + obliqueWorldPad;

  const baselineOffsetFromBottom =
    block.lines.length > 0
      ? Math.max(
          0,
          (block.canvasHeight - (block.lines[0]?.canvasBaselineY ?? 0)) *
            (block.worldHeight / Math.max(block.canvasHeight, 1e-9))
        )
      : 0;

  const worldOffsets = computeAttachmentOffsets(
    {
      width: worldWidth,
      height: block.worldHeight,
      baselineOffsetFromBottom,
    },
    textObject.horizontalAttachment,
    textObject.verticalAttachment
  );

  const lines: TextMeshLineLayout[] = block.lines.map((line) => ({
    text: line.text,
    canvasX: CANVAS_PADDING + obliqueLeftPadPx + line.canvasX,
    canvasBaselineY: CANVAS_PADDING + line.canvasBaselineY,
  }));

  return {
    lines,
    canvasFontPx,
    canvasWidth: paddedCanvasWidth,
    canvasHeight,
    width: worldWidth,
    height: block.worldHeight,
    ...worldOffsets,
  };
}

function drawTextToCanvas(
  context: CanvasRenderingContext2D,
  layout: TextMeshLayout,
  textObject: ITextObject
): void {
  context.clearRect(0, 0, layout.canvasWidth, layout.canvasHeight);
  context.font = buildCanvasFont(layout.canvasFontPx, textObject.typefaceName);
  context.textBaseline = 'alphabetic';
  context.fillStyle = textObject.color;

  const drawDirection = textObject.drawDirection;
  const obliqueAngle = textObject.obliqueAngle;
  const widthFactor = resolveWidthFactor(textObject.widthFactor);
  const hasOblique = Math.abs(obliqueAngle) > 1e-9;
  const hasWidthFactor = Math.abs(widthFactor - 1) > 1e-9;
  for (const line of layout.lines) {
    context.save();
    context.translate(line.canvasX, line.canvasBaselineY);
    if (hasWidthFactor) {
      context.scale(widthFactor, 1);
    }
    if (hasOblique) {
      context.transform(1, 0, -Math.tan(obliqueAngle), 1, 0, 0);
    }
    const drawText =
      drawDirection === 'rightToLeft'
        ? [...line.text].reverse().join('')
        : line.text;
    context.fillText(drawText, 0, 0);
    context.restore();
  }
}

export class TextMesh extends Group {
  private readonly mesh: Mesh;
  private texture: Texture | undefined;
  private drawCanvas: HTMLCanvasElement | undefined;
  private drawContext: CanvasRenderingContext2D | undefined;
  private material: MeshBasicMaterial | undefined;
  private planeGeometry: PlaneGeometry | undefined;
  private lastLayout: TextMeshLayout | undefined;
  private lastWorldWidth = 0;
  private lastWorldHeight = 0;

  constructor(private textObject: ITextObject) {
    super();
    this.mesh = new Mesh();
    this.add(this.mesh);
    this.userData['textGuid'] = textObject.guid;
    this.name = textObject.guid;
    this.rebuildAppearance();
    this.applyTransform();
  }

  public get guid(): string {
    return this.textObject.guid;
  }

  public update(textObject: ITextObject): void {
    const previous = this.textObject;
    this.textObject = textObject;
    this.userData['textGuid'] = textObject.guid;
    this.name = textObject.guid;

    const appearanceChanged = !textAppearanceEqual(previous, textObject);
    const transformChanged = !textTransformEqual(previous, textObject);

    if (appearanceChanged) {
      this.rebuildAppearance();
    } else if (transformChanged) {
      this.applyTransform();
    }
  }

  public setInsertionPointPreview(point: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
  }): void {
    this.position.set(point.x, point.y, point.z);
  }

  public dispose(): void {
    this.planeGeometry?.dispose();
    this.material?.map?.dispose();
    this.material?.dispose();
    this.texture?.dispose();
    this.planeGeometry = undefined;
    this.material = undefined;
    this.texture = undefined;
    this.drawCanvas = undefined;
    this.drawContext = undefined;
    this.lastLayout = undefined;
  }

  private ensureDrawCanvas(
    width: number,
    height: number
  ):
    | {
        context: CanvasRenderingContext2D;
        canvasResized: boolean;
      }
    | undefined {
    if (!this.drawCanvas) {
      this.drawCanvas = document.createElement('canvas');
      this.drawContext = this.drawCanvas.getContext('2d') ?? undefined;
    }
    if (!this.drawContext) {
      return undefined;
    }
    let canvasResized = false;
    if (this.drawCanvas.width !== width) {
      this.drawCanvas.width = width;
      canvasResized = true;
    }
    if (this.drawCanvas.height !== height) {
      this.drawCanvas.height = height;
      canvasResized = true;
    }
    return { context: this.drawContext, canvasResized };
  }

  private rebuildAppearance(): void {
    const layout = measureTextMesh(this.textObject);
    this.lastLayout = layout;

    const canvasState = this.ensureDrawCanvas(
      layout.canvasWidth,
      layout.canvasHeight
    );
    if (!canvasState) {
      return;
    }

    drawTextToCanvas(canvasState.context, layout, this.textObject);

    // Three.js allocates the GPU texture at the size of the canvas when
    // Texture is created; when the canvas is resized (e.g. content grows and
    // needs a taller image) needsUpdate alone re-uploads pixels into the
    // original GPU dimensions, which stretches the newly-larger canvas into
    // the old size. Recreate the Texture whenever the canvas backing was
    // resized so the GPU allocation matches.
    if (!this.texture || canvasState.canvasResized) {
      this.texture?.dispose();
      this.texture = new Texture(this.drawCanvas);
      if (this.material) {
        this.material.map = this.texture;
        this.material.needsUpdate = true;
      }
    }
    this.texture.needsUpdate = true;

    this.updateGeometry(layout.width, layout.height);
    this.updateMaterial();
    this.mesh.renderOrder = TEXT_RENDER_ORDER;
    this.applyTransform();
  }

  private updateGeometry(worldWidth: number, worldHeight: number): void {
    const widthChanged =
      Math.abs(worldWidth - this.lastWorldWidth) > GEOMETRY_SIZE_EPSILON;
    const heightChanged =
      Math.abs(worldHeight - this.lastWorldHeight) > GEOMETRY_SIZE_EPSILON;

    if (!this.planeGeometry || widthChanged || heightChanged) {
      this.planeGeometry?.dispose();
      this.planeGeometry = new PlaneGeometry(worldWidth, worldHeight);
      this.mesh.geometry = this.planeGeometry;
      this.lastWorldWidth = worldWidth;
      this.lastWorldHeight = worldHeight;
      return;
    }

    this.mesh.geometry = this.planeGeometry;
  }

  private updateMaterial(): void {
    if (!this.texture) {
      return;
    }

    if (!this.material) {
      this.material = new MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        side: TEXT_MESH_MATERIAL_SIDE,
        depthTest: false,
        depthWrite: false,
      });
      this.mesh.material = this.material;
      return;
    }

    this.material.map = this.texture;
    this.material.needsUpdate = true;
    this.mesh.material = this.material;
  }

  private applyTransform(): void {
    const layout = this.lastLayout;
    if (!layout) {
      return;
    }

    const { insertionPoint, rotation } = this.textObject;
    this.position.set(insertionPoint.x, insertionPoint.y, insertionPoint.z);
    this.rotation.set(0, 0, rotation);

    const localOffsetX = layout.offsetX + layout.width / 2;
    const localOffsetY = layout.offsetY + layout.height / 2;
    this.mesh.position.set(localOffsetX, localOffsetY, 0);
  }
}
